import {
  Box,
  H6,
  Image,
  NeoColor,
  Stack,
  Text,
  ThemeProvider,
  neoTheme,
} from "@noom/wax-component-library";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DEEP_LINK_BASE } from "@modules/common/constants";

import marketplaceHeaderBannerFree from "../../image_assets/marketplace_header_banner_free.svg";
import {
  trackCareTreatmentCardTapped,
  trackMedTreatmentStatusCtaTapped,
} from "../nativeAnalytics/medAnalytics";

import { CareTabTreatmentList } from "./CareTabTreatmentList";
import { useCareTabFocusRefresh } from "./hooks/useCareTabFocusRefresh";
import { useHideDocumentScrollbar } from "./hooks/useHideDocumentScrollbar";
import {
  type UseMarketplaceAdsResult,
  useMarketplaceAds,
} from "./hooks/useMarketplaceAds";
import {
  type MarketplaceSectionHandlers,
  MarketplaceSectionView,
  useMarketplaceSectionHandlers,
} from "./marketplace/MarketplaceSection";
import { TreatmentCardError } from "./TreatmentCardError";
import { TreatmentCardSkeleton } from "./TreatmentCardSkeleton";
import { TreatmentDetailBottomSheet } from "./TreatmentDetailBottomSheet";
import {
  type TreatmentOverview,
  type UseTreatmentOverviewsResult,
  useTreatmentOverviews,
} from "./hooks/useTreatmentOverviews";
import {
  type GetTreatmentCardPropsHandlers,
  getTreatmentBottomSheetConfig,
} from "./treatmentCardMapper";
import {
  isOtcTreatmentOverview,
  isRxTreatmentOverview,
} from "./treatmentOverviewGuards";
import { openDeeplink } from "./utils";

const NATIVE_PRESCRIPTIONS_DEEPLINK = `${DEEP_LINK_BASE}?open=YOUR_PRESCRIPTIONS`;

/**
 * Build-time Hive flags, with optional Cypress overrides via
 * `window.__careFlags` (see cypress/support/careNativeHooks.ts).
 */
const shouldOpenNativePrescriptionPage = () => {
  const override = window.__careFlags?.nativePrescriptionPage;
  if (override !== undefined) {
    return override;
  }
  return process.env.HIVE_CARE_NATIVE_PRESCRIPTION_PAGE === "true";
};

/** Staging/TEST: `"true"`. Production: `"false"` until rollout. */
const isSupplementTreatmentDetailEnabled = () => {
  const override = window.__careFlags?.supplementTreatmentDetail;
  if (override !== undefined) {
    return override;
  }
  return process.env.HIVE_CARE_SUPPLEMENT_TREATMENT_DETAIL === "true";
};

/** Staging/TEST: `"true"`. Production: `"false"` until rollout. */
const isBiomarkerTreatmentDetailEnabled = () => {
  const override = window.__careFlags?.biomarkerTreatmentDetail;
  if (override !== undefined) {
    return override;
  }
  return process.env.HIVE_CARE_BIOMARKER_TREATMENT_DETAIL === "true";
};

type CareTabPageViewProps = {
  treatmentResult: UseTreatmentOverviewsResult;
  marketplaceResult: UseMarketplaceAdsResult;
  marketplaceHandlers: MarketplaceSectionHandlers;
};

export const CareTabPageView = ({
  treatmentResult,
  marketplaceResult,
  marketplaceHandlers,
}: CareTabPageViewProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hideTitle = searchParams.get("hideTitle") === "true";
  const {
    t,
    i18n,
    ready = true,
  } = useTranslation("care", {
    useSuspense: false,
  });
  const {
    treatments,
    headerTitleKey,
    loading,
    error,
    refetch: refetchTreatments,
  } = treatmentResult;
  const {
    placements,
    headerImageUrl,
    loading: marketplaceLoading,
    error: marketplaceError,
  } = marketplaceResult;
  const hasMarketplaceContent = placements.length > 0;
  const hasMarketplaceSheet =
    marketplaceLoading || !!marketplaceError || hasMarketplaceContent;
  const [selectedTreatment, setSelectedTreatment] = useState<
    TreatmentOverview | undefined
  >(undefined);
  const lastSheetTreatment = useRef<TreatmentOverview | undefined>(undefined);
  const supplementDetailEnabled = isSupplementTreatmentDetailEnabled();
  const biomarkerDetailEnabled = isBiomarkerTreatmentDetailEnabled();

  if (selectedTreatment) {
    lastSheetTreatment.current = selectedTreatment;
  }

  const renderedSheetTreatment =
    selectedTreatment ?? lastSheetTreatment.current;
  const sheetConfig = renderedSheetTreatment
    ? getTreatmentBottomSheetConfig(renderedSheetTreatment, t, {
        isSupplementTreatmentDetailEnabled: supplementDetailEnabled,
        isBiomarkerTreatmentDetailEnabled: biomarkerDetailEnabled,
      })
    : undefined;
  const isSheetOpen = !!selectedTreatment && !!sheetConfig;

  const closeSheet = () => setSelectedTreatment(undefined);

  const cardHandlers: GetTreatmentCardPropsHandlers = {
    onShowBottomSheet: setSelectedTreatment,
    onCardTapped: ({ treatmentName }) =>
      void trackCareTreatmentCardTapped(treatmentName),
    onOpenTreatmentDetail: (treatment) => {
      // Native prescriptions is an Rx-only interim. Supplements that reach
      // this handler (via HIVE_CARE_SUPPLEMENT_TREATMENT_DETAIL) must still
      // open `/care/treatment/:treatmentCode`.
      if (
        shouldOpenNativePrescriptionPage() &&
        isRxTreatmentOverview(treatment)
      ) {
        openDeeplink(NATIVE_PRESCRIPTIONS_DEEPLINK);
        return;
      }

      if (!treatment.treatmentCode) {
        return;
      }

      // Pass the already-loaded `treatment` via `location.state` so the
      // detail page renders instantly while its cache-backed query subscribes
      // for focus refreshes. Deeplink scenarios have no preloaded state and
      // render from that query directly.
      //
      // Carry this page's query string along so presentation params (e.g.
      // `hideTitle`) survive the round trip -- `DetailShell`'s back button
      // replays them onto `/care`.
      const search = searchParams.toString();
      navigate(
        {
          pathname: `/care/treatment/${encodeURIComponent(
            treatment.treatmentCode
          )}`,
          search: search ? `?${search}` : "",
        },
        { state: { treatment } }
      );
    },
    onStatusRowCtaClick: (meta) =>
      void trackMedTreatmentStatusCtaTapped({
        ...meta,
        context: "status_row",
      }),
  };

  const isUserWithoutTreatments = !loading && !error && treatments.length === 0;

  const hasRxTreatment = treatments.some(isRxTreatmentOverview);
  const programTitle =
    !hasRxTreatment &&
    headerTitleKey &&
    i18n.exists(`programCardTitle.${headerTitleKey}`, { ns: "care" })
      ? t(`programCardTitle.${headerTitleKey}`)
      : undefined;

  const showHeader = !hideTitle || !!programTitle;
  const isPageHeaderVisible =
    ready &&
    showHeader &&
    !(
      (isUserWithoutTreatments || loading) &&
      (marketplaceLoading || hasMarketplaceContent)
    );
  const treatmentColumnHasContent =
    !ready || loading || !!error || treatments.length > 0;

  return (
    <ThemeProvider theme={neoTheme}>
      <Box bg={NeoColor.greyscale.lightGray} minH="100vh">
        <Stack
          spacing={4}
          width="100%"
          maxWidth="480px"
          margin="0 auto"
          minHeight="100vh"
        >
          {isPageHeaderVisible && (
            <Stack
              width="100%"
              px={4}
              pt={4}
              isInline
              align="center"
              justify="space-between"
            >
              {!hideTitle && <H6 fontWeight="400">{t("pageTitle")}</H6>}
              {programTitle && (
                <Text
                  fontSize="xs"
                  fontWeight="400"
                  color={NeoColor.greyscale.black}
                  letterSpacing="0.03px"
                >
                  {programTitle}
                </Text>
              )}
            </Stack>
          )}
          <Stack
            spacing={4}
            width="100%"
            px={4}
            pt={
              !isPageHeaderVisible && treatmentColumnHasContent ? 4 : 0
            }
            pb={treatmentColumnHasContent && hasMarketplaceSheet ? 4 : 0}
          >
            {!ready || loading ? (
              <div data-cy="care-loading">
                <TreatmentCardSkeleton />
              </div>
            ) : error ? (
              <div data-cy="care-error">
                <TreatmentCardError
                  message={t("errorState.connectionLost")}
                  retryLabel={t("errorState.retry")}
                  onRetry={refetchTreatments}
                />
              </div>
            ) : (
              <CareTabTreatmentList
                treatments={treatments}
                handlers={cardHandlers}
                isSupplementTreatmentDetailEnabled={supplementDetailEnabled}
                isBiomarkerTreatmentDetailEnabled={biomarkerDetailEnabled}
              />
            )}
          </Stack>
          {ready &&
            isUserWithoutTreatments &&
            hasMarketplaceContent &&
            !marketplaceLoading && (
              // Wait for the marketplace query to settle before rendering so
              // the FREE banner is used only as a genuine fallback (when the
              // response has no `headerImageUrl`) rather than flashing while
              // the remote URL loads. The negative bottom margin overlaps the
              // image over the marketplace sheet's rounded top corners so the
              // image meets its straight edge with no gap.
              <Image
                src={headerImageUrl || marketplaceHeaderBannerFree}
                alt=""
                width="100%"
                mt={-4}
                mb={-8}
                data-cy="marketplace-header-banner"
              />
            )}
          {ready && (
            <MarketplaceSectionView
              {...marketplaceResult}
              {...marketplaceHandlers}
            />
          )}
        </Stack>
        <TreatmentDetailBottomSheet
          treatment={renderedSheetTreatment}
          isOpen={ready && isSheetOpen}
          onClose={closeSheet}
          body={sheetConfig?.body}
          ctaLabel={sheetConfig?.ctaLabel}
          ctaVariant={sheetConfig?.ctaVariant}
          ctaHref={
            sheetConfig?.deeplinkOpenMode === "external"
              ? sheetConfig.deeplink
              : undefined
          }
          showProgramName={
            !renderedSheetTreatment ||
            !isOtcTreatmentOverview(renderedSheetTreatment)
          }
          onConfirm={() => {
            if (sheetConfig && renderedSheetTreatment) {
              void trackMedTreatmentStatusCtaTapped({
                ctaAction: sheetConfig.ctaAction,
                ctaText: sheetConfig.ctaLabel,
                medicationName: renderedSheetTreatment.treatmentName,
                context: "bottom_sheet",
              });
              if (
                sheetConfig.deeplink &&
                sheetConfig.deeplinkOpenMode !== "external"
              ) {
                openDeeplink(sheetConfig.deeplink);
              }
            }
            closeSheet();
          }}
        />
      </Box>
    </ThemeProvider>
  );
};

export const CareTabPage = () => {
  useHideDocumentScrollbar();
  const treatmentResult = useTreatmentOverviews();
  const marketplaceResult = useMarketplaceAds();
  const marketplaceHandlers = useMarketplaceSectionHandlers();

  useCareTabFocusRefresh(() => {
    treatmentResult.refetch();
    marketplaceResult.refetch();
  });

  return (
    <CareTabPageView
      treatmentResult={treatmentResult}
      marketplaceResult={marketplaceResult}
      marketplaceHandlers={marketplaceHandlers}
    />
  );
};
